"use client";
import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { createContext, useState, useEffect, useContext } from "react";
import { fetchFooterData } from "../../data/cdn.js";
import { fallbackFooterData } from "../../data/footer.js";
import { emitComponentLoadEvent, emitLinkClickEvent, emitUserInteractionEvent } from "../../utils/tracking.js";
import { resolveURL } from "../../utils/url.js";
import { ErrorBoundary } from "../ErrorBoundary.js";
import { Logo } from "../Logo.js";
import { AlphabetNav } from "./AlphabetNav/AlphabetNav.js";
import styles from "./Footer.module.scss.js";
import { SEOLinks } from "./SEOLinks/SEOLinks.js";
const FooterContext = createContext(null);
const useFooterContext = () => {
  const context = useContext(FooterContext);
  if (!context) {
    throw new Error(
      "useFooterContext must be used within a FooterContext.Provider"
    );
  }
  return context;
};
function FooterInner(props) {
  const [data, setData] = useState(
    props.data ?? fallbackFooterData
  );
  useEffect(() => {
    if (!props.disableDataFetching) {
      fetchFooterData({ baseURL: props.dataBaseURL, staging: props.staging }).then(setData).catch((err) => console.error("[@zapier/marketing-site-layout]", err));
    }
  }, [props.disableDataFetching, props.dataBaseURL, props.staging]);
  useEffect(() => {
    emitComponentLoadEvent({
      component_name: "Footer"
    });
  }, []);
  const resolveURL$1 = (url = "") => {
    return resolveURL(url, props.baseURL, props.currentPath);
  };
  return /* @__PURE__ */ jsx(FooterContext.Provider, { value: { resolveURL: resolveURL$1 }, children: /* @__PURE__ */ jsx("footer", { className: styles.root, children: /* @__PURE__ */ jsxs("div", { className: styles.content, children: [
    props.showAlphabetNav && /* @__PURE__ */ jsx(AlphabetNav, {}),
    props.showSEOLinks && /* @__PURE__ */ jsx(SEOLinks, { sections: data.seoLinkSections }),
    /* @__PURE__ */ jsxs("div", { className: styles.section, children: [
      /* @__PURE__ */ jsx("div", { className: styles.social, children: data.socialLinks.length > 0 && /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx("div", { className: styles.socialLabel, children: "Follow us" }),
        /* @__PURE__ */ jsx("ul", { className: styles.socialList, children: data.socialLinks.map((link, index) => /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsx(
          "a",
          {
            className: styles.socialLink,
            href: resolveURL$1(link.url),
            "aria-label": link.label,
            style: {
              "--social-link-background-color": link.icon.backgroundColor
            },
            onClick: (e) => {
              emitLinkClickEvent(e, {
                interaction_name: "footer_social_link_click"
              });
            },
            ...link.openInNewTab && {
              target: "_blank",
              rel: "noreferrer"
            },
            children: /* @__PURE__ */ jsx(
              "img",
              {
                className: styles.socialIcon,
                src: link.icon.url,
                alt: link.icon.alt
              }
            )
          }
        ) }, index)) })
      ] }) }),
      /* @__PURE__ */ jsx("nav", { "aria-label": "Resource footer links", children: /* @__PURE__ */ jsx("ul", { className: styles.list, children: data.resourceLinks.map((link, index) => /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsx(
        "a",
        {
          className: styles.resourceLink,
          href: resolveURL$1(link.url),
          onClick: (e) => {
            emitLinkClickEvent(e, {
              interaction_name: "footer_resource_link_click"
            });
          },
          ...link.openInNewTab && {
            target: "_blank",
            rel: "noreferrer"
          },
          children: link.label
        }
      ) }, index)) }) })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: styles.section, children: [
      /* @__PURE__ */ jsx(
        "a",
        {
          className: styles.logo,
          href: resolveURL$1("/"),
          "aria-label": "Zapier",
          onClick: (e) => {
            emitLinkClickEvent(e, {
              interaction_name: "footer_logo_click"
            });
          },
          children: /* @__PURE__ */ jsx(Logo, {})
        }
      ),
      /* @__PURE__ */ jsxs("div", { className: styles.legal, children: [
        /* @__PURE__ */ jsxs("div", { children: [
          "© ",
          (/* @__PURE__ */ new Date()).getFullYear(),
          " Zapier Inc."
        ] }),
        /* @__PURE__ */ jsx(
          "button",
          {
            className: `${styles.legalLink} ot-sdk-show-settings`,
            style: { display: "none" },
            onClick: () => {
              emitUserInteractionEvent({
                event_action: "click",
                interaction_name: "footer_manage_cookies_click",
                interaction_raw_text: "Manage cookies"
              });
            },
            children: "Manage cookies"
          }
        ),
        /* @__PURE__ */ jsx("nav", { "aria-label": "Legal and disclaimer links", children: /* @__PURE__ */ jsx("ul", { className: styles.list, children: data.legalLinks.map((link, index) => /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsx(
          "a",
          {
            className: styles.legalLink,
            href: resolveURL$1(link.url),
            onClick: (e) => {
              emitLinkClickEvent(e, {
                interaction_name: "footer_legal_link_click"
              });
            },
            ...link.openInNewTab && {
              target: "_blank",
              rel: "noreferrer"
            },
            children: link.label
          }
        ) }, index)) }) })
      ] })
    ] })
  ] }) }) });
}
function FallbackFooter() {
  return /* @__PURE__ */ jsx("footer", { className: styles.root, children: /* @__PURE__ */ jsx("div", { className: styles.content, children: /* @__PURE__ */ jsxs("div", { className: styles.section, children: [
    /* @__PURE__ */ jsx(
      "a",
      {
        className: styles.logo,
        href: "https://zapier.com/",
        "aria-label": "Zapier",
        children: /* @__PURE__ */ jsx(Logo, {})
      }
    ),
    /* @__PURE__ */ jsxs("div", { className: styles.legal, children: [
      /* @__PURE__ */ jsxs("div", { children: [
        "© ",
        (/* @__PURE__ */ new Date()).getFullYear(),
        " Zapier Inc."
      ] }),
      /* @__PURE__ */ jsx(
        "button",
        {
          className: `${styles.legalLink} ot-sdk-show-settings`,
          style: { display: "none" },
          children: "Manage cookies"
        }
      ),
      /* @__PURE__ */ jsx("nav", { "aria-label": "Legal and disclaimer links", children: /* @__PURE__ */ jsxs("ul", { className: styles.list, children: [
        /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsx(
          "a",
          {
            className: styles.legalLink,
            href: "https://zapier.com/legal",
            children: "Legal"
          }
        ) }),
        /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsx(
          "a",
          {
            className: styles.legalLink,
            href: "https://zapier.com/privacy",
            children: "Privacy"
          }
        ) })
      ] }) })
    ] })
  ] }) }) });
}
function Footer(props) {
  return /* @__PURE__ */ jsx(ErrorBoundary, { name: "Footer", renderFallback: () => /* @__PURE__ */ jsx(FallbackFooter, {}), children: /* @__PURE__ */ jsx(FooterInner, { ...props }) });
}
export {
  FallbackFooter,
  Footer,
  useFooterContext
};
